import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { WhatsApp, ContentCopy } from "@mui/icons-material";
import { useSendInvitationMutation } from "api/endpoints";

interface InviteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invitationLink: string;
}

export const InviteDialogComponent: React.FC<InviteDialogProps> = ({
  isOpen,
  onClose,
  invitationLink,
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userName, setUserName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const [sendInvitation, { isLoading }] = useSendInvitationMutation();

  const handleSubmit = async () => {
    if (!phoneNumber.trim() || !userName.trim()) {
      setError("لطفاً تمام فیلدها را پر کنید");
      return;
    }

    // Validate phone number format
    const phoneRegex = /^(\+98|0)?9\d{9}$/;
    if (!phoneRegex.test(phoneNumber.replace(/\s/g, ""))) {
      setError("شماره تلفن معتبر نیست");
      return;
    }

    try {
      setError("");
      await sendInvitation({
        phoneNumber: phoneNumber.replace(/\s/g, ""),
        userName: userName.trim(),
        invitationLink,
      }).unwrap();

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.data?.message || "خطا در ارسال پیام");
    }
  };

  const handleClose = () => {
    setPhoneNumber("");
    setUserName("");
    setError("");
    setSuccess(false);
    setCopySuccess(false);
    onClose();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <WhatsApp color="primary" />
          <Typography variant="h6" component="div">
            ارسال لینک دعوت از طریق واتساپ
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            لینک دعوت با موفقیت ارسال شد! ✅
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              لینک دعوت برای کاربر ارسال خواهد شد:
            </Typography>

            <Box
              sx={{
                p: 2,
                backgroundColor: "grey.50",
                borderRadius: 2,
                mb: 3,
                border: "1px solid",
                borderColor: "grey.200",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  direction: "ltr",
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  color: "primary.main",
                  flex: 1,
                }}
              >
                {invitationLink}
              </Typography>
              <Tooltip title={copySuccess ? "کپی شد!" : "کپی کردن لینک"}>
                <IconButton
                  onClick={handleCopyLink}
                  size="small"
                  sx={{
                    color: copySuccess ? "success.main" : "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.50",
                    },
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <TextField
              fullWidth
              label="نام کاربر"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="نام کامل کاربر را وارد کنید"
            />

            <TextField
              fullWidth
              label="شماره تلفن"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="09123456789"
              helperText="شماره تلفن را با فرمت 09123456789 وارد کنید"
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          {success ? "بستن" : "انصراف"}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress className="icon-btn" size={20} />
              ) : (
                <WhatsApp className="icon-btn" />
              )
            }
          >
            {isLoading ? "در حال ارسال..." : "ارسال از طریق واتساپ"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
